<template>
  <v-row align="center" :dense="$vuetify.breakpoint.mdAndUp" class="date-range-selector">
    <v-col cols="12" md="4">
      <v-menu offset-y>
        <template v-slot:activator="{ on, attrs }">
          <v-btn outlined block v-bind="attrs" v-on="on" class="preset-button">
            <v-icon size="24" left>mdi-calendar-clock</v-icon>
            {{ selectedRangeLabel }}
            <v-spacer />
            <v-icon size="24" right>mdi-menu-down</v-icon>
          </v-btn>
        </template>

        <v-list :dense="$vuetify.breakpoint.mdAndUp">
          <v-list-item
            v-for="preset in presets"
            :key="preset.value"
            :class="{ 'primary--text': isPresetSelected(preset.value) }"
            @click="selectPreset(preset)"
          >
            <v-list-item-icon v-if="isPresetSelected(preset.value)" class="mx-0 mr-2">
              <v-icon color="primary" small>mdi-check</v-icon>
            </v-list-item-icon>
            <v-list-item-title :class="{ 'pl-8': !isPresetSelected(preset.value) }">
              {{ preset.label }}
            </v-list-item-title>
          </v-list-item>

          <v-divider class="my-1" />

          <v-list-item :class="{ 'primary--text': show_custom }" @click="toggleCustomRange">
            <v-list-item-icon v-if="show_custom" class="mx-0 mr-2">
              <v-icon color="primary" small>mdi-check</v-icon>
            </v-list-item-icon>
            <v-list-item-title :class="{ 'pl-8': !show_custom }">
              Custom Range...
            </v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </v-col>

    <template v-if="show_custom">
      <v-col cols="12" md="4">
        <v-menu
          v-model="date_start_menu"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          min-width="auto"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              :value="startFormatted"
              label="Start Date"
              append-icon="mdi-calendar"
              readonly
              v-bind="attrs"
              v-on="on"
              outlined
              dense
              hide-details
              class="date-picker-activator"
            />
          </template>
          <v-date-picker
            :value="internal_start"
            :max="internal_end || today"
            color="primary"
            no-title
            scrollable
            @input="handleStartChange"
          />
        </v-menu>
      </v-col>

      <v-col cols="12" md="4">
        <v-menu
          v-model="date_stop_menu"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          min-width="auto"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              :value="endFormatted"
              label="End Date"
              append-icon="mdi-calendar"
              readonly
              v-bind="attrs"
              v-on="on"
              outlined
              dense
              hide-details
              class="date-picker-activator"
            />
          </template>
          <v-date-picker
            :value="internal_end"
            :min="internal_start"
            :max="today"
            color="primary"
            no-title
            scrollable
            @input="handleEndChange"
          />
        </v-menu>
      </v-col>
    </template>
  </v-row>
</template>

<script>
export default {
  name: 'DateRangeSelector',

  props: {
    value: {
      type: Object,
      required: true,
      validator: value => {
        return ['start', 'end'].every(key => key in value);
      },
    },

    presets: {
      type: Array,
      default: () => [
        { label: 'Last 7 Days', value: 'last7Days', days: 7 },
        { label: 'Last 30 Days', value: 'last30Days', days: 30 },
        { label: 'Last 90 Days', value: 'last90Days', days: 90 },
      ],
    },

    max_range_days: {
      type: Number,
      default: 180,
    },
  },

  data() {
    return {
      date_start_menu: false,
      date_stop_menu: false,
      internal_start: this.value.start,
      internal_end: this.value.end,
      current_preset: 'last30Days',
      show_custom: false,
    };
  },

  computed: {
    today() {
      return this.formatDateForPicker(new Date());
    },

    startFormatted() {
      if (!this.internal_start) return null;
      const [year, month, day] = this.internal_start.split('-').map(Number);
      const local_date = new Date(year, month - 1, day);
      return this.$rc.pretty(local_date, 'date');
    },

    endFormatted() {
      if (!this.internal_end) return null;
      const [year, month, day] = this.internal_end.split('-').map(Number);
      const local_date = new Date(year, month - 1, day);
      return this.$rc.pretty(local_date, 'date');
    },

    selectedPresetLabel() {
      if (!this.current_preset) return null;
      const preset = this.presets.find(p => p.value === this.current_preset);
      return preset ? preset.label : null;
    },

    selectedRangeLabel() {
      if (this.show_custom) {
        return 'Custom Range';
      }
      return this.selectedPresetLabel || 'Select Date Range';
    },
  },

  watch: {
    value: {
      handler(new_value) {
        this.internal_start = new_value.start;
        this.internal_end = new_value.end;
      },
      deep: true,
    },
  },

  methods: {
    formatDateForPicker(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },

    handleStartChange(date) {
      this.internal_start = date;
      this.date_start_menu = false;
      this.current_preset = null;
      this.enforceMaxDateRange();
      this.emitChange();
    },

    handleEndChange(date) {
      this.internal_end = date;
      this.date_stop_menu = false;
      this.current_preset = null;
      this.enforceMaxDateRange();
      this.emitChange();
    },

    enforceMaxDateRange() {
      if (!this.internal_start || !this.internal_end) return;

      const start_date = new Date(this.internal_start);
      const end_date = new Date(this.internal_end);
      const days_diff = Math.ceil((end_date - start_date) / (1000 * 60 * 60 * 24));

      if (days_diff > this.max_range_days) {
        const new_end_date = new Date(start_date);
        new_end_date.setDate(start_date.getDate() + this.max_range_days);
        this.internal_end = this.formatDateForPicker(new_end_date);

        this.$rc.snackbar(
          `Date range limited to ${this.max_range_days} days. End date adjusted to ${this.endFormatted}.`
        );
      }
    },

    selectPreset(preset) {
      const today = new Date();
      let start_date;

      if (preset.isYTD) {
        start_date = new Date(today.getFullYear(), 0, 1);
      } else {
        start_date = new Date(today);
        start_date.setDate(today.getDate() - preset.days + 1);
      }

      this.internal_start = this.formatDateForPicker(start_date);
      this.internal_end = this.formatDateForPicker(today);
      this.current_preset = preset.value;
      this.show_custom = false;
      this.emitChange();
    },

    toggleCustomRange() {
      this.show_custom = true;
      this.current_preset = null;
    },

    isPresetSelected(presetValue) {
      return this.current_preset === presetValue;
    },

    emitChange() {
      this.$emit('input', {
        start: this.internal_start,
        end: this.internal_end,
      });
      this.$emit('change', {
        start: this.internal_start,
        end: this.internal_end,
      });
    },

    resetToDefault() {
      const default_preset = this.presets.find(p => p.value === 'last30Days');
      if (default_preset) {
        this.selectPreset(default_preset);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.date-range-selector {
  .date-picker-activator {
    cursor: pointer;
  }

  .preset-button {
    height: 40px;
    text-transform: none;
    letter-spacing: normal;
    font-weight: normal;
    font-size: 1rem;
    border-color: rgba(0, 0, 0, 0.38); /* match that of v-select */
  }

  .v-icon {
    color: rgba(0, 0, 0, 0.54); /* match that of v-select */
  }
}
</style>
